import React from 'react';

import {createBrowserRouter} from 'react-router-dom';
import constants from './constant.ts';
import {MainLayout} from '@/layouts';
import SessionGuard from '@/auth/session-guard';

const LoginPage = React.lazy(() => import('@/pages/login'));
const LogsPage = React.lazy(() => import('@/pages/logs'));
const ErrorPage = React.lazy(() => import('@/pages/error-page'));
const PageNotFound = React.lazy(() => import('@/pages/page-not-found'));
const HomePage = React.lazy(() => import('@/pages/home'));
const SettingsPage = React.lazy(() => import('@/pages/settings'));
const SwaggerApiPage = React.lazy(() => import('@/pages/swagger-api'));
const ConnectionsPage = React.lazy(() => import('@/pages/connections.tsx'));
const CredentialsPage = React.lazy(() => import('@/pages/credentials'));
const IdentifiersPage = React.lazy(() => import('@/pages/identifiers'));
const NotificationsPage = React.lazy(() => import('@/pages/notifications'));

import DescriptionIcon from '@mui/icons-material/Description';
import HomeIcon from '@mui/icons-material/Home';
import SettingsIcon from '@mui/icons-material/Settings';
import DataObjectIcon from '@mui/icons-material/DataObject';
import WalletIcon from '@mui/icons-material/Wallet';
import ContactsIcon from '@mui/icons-material/Contacts';
import KeyIcon from '@mui/icons-material/Key';
import RememberMeIcon from '@mui/icons-material/RememberMe';
import NotificationsIcon from '@mui/icons-material/Notifications';


// export type EnvVariable = {
//   value: string | number | boolean;
//   editable: boolean;
// };
//
// export type EnvResponse = Record<string, EnvVariable>;


type RouteChild = { path: string, label: string, showInHeader: boolean, icon: React.ReactNode, children?: RouteNode }
type RouteNode = Record<string, RouteChild>

export const ROUTE_CONFIG: RouteNode = {
  HOME: {
    path: '/',
    label: 'Home',
    showInHeader: true,
    icon: <HomeIcon />,
  },
  WALLET: {
    path: '/wallet',
    label: 'Wallet',
    showInHeader: true,
    icon: <WalletIcon />,
    children: {
      CONNECTIONS: {
        path: '/connections',
        label: 'Connections',
        showInHeader: true,
        icon: <ContactsIcon />,
      },
      CREDENTIALS: {
        path: '/credentials',
        label: 'Credentials',
        showInHeader: true,
        icon: <KeyIcon/>,
      },
      IDENTIFIERS: {
        path: '/identifiers',
        label: 'Identifiers',
        showInHeader: true,
        icon: <RememberMeIcon />,
      },
    }
  },
  NOTIFICATIONS: {
    path: '/notifications',
    label: 'Notifications',
    showInHeader: true,
    icon: <NotificationsIcon />,
  },
  LOGS: {
    path: '/logs',
    label: 'Activity',
    showInHeader: true,
    icon: <DescriptionIcon />
  },
  SWAGGER_API: {
    path: '/swagger-api',
    label: 'Swagger api',
    showInHeader: true,
    icon: <DataObjectIcon />
  },
  SETTINGS: {
    path: '/settings',
    label: 'Settings',
    showInHeader: true,
    icon: <SettingsIcon />,
  },
}

function mapRouteConfigToNavLinks(config: any): NAV_LINK_TYPE[] {
  return Object.values(config)
    .filter((route: any) => route.showInHeader)
    .map((route: any): NAV_LINK_TYPE => ({
      label: route.label,
      to: route.path,
      icon: route.icon,
      children: route.children ? mapRouteConfigToNavLinks(route.children) : []
    }));
}

export type NAV_LINK_TYPE = { label :string, to: string, icon: React.ReactNode, children?: NAV_LINK_TYPE[] }

export const NAV_LINKS: NAV_LINK_TYPE[] = mapRouteConfigToNavLinks(ROUTE_CONFIG);

// SessionGuard wraps the whole authenticated app. With direct-auth off it is a
// pass-through; with it on, it redirects to /login until unlocked. /login sits
// outside the guard (and outside MainLayout) so it renders standalone.
const router = [
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    element: <SessionGuard />,
    children: [
  {
    path: ROUTE_CONFIG.HOME.path,
    element: <MainLayout />,
    errorElement: (
      <MainLayout>
        <ErrorPage />
      </MainLayout>
    ),
    children: [
      {element: <HomePage />, path: ROUTE_CONFIG.HOME.path, index: true},
      {
        element: <LogsPage />,
        path: ROUTE_CONFIG.LOGS.path,
      },
      {
        element: <SwaggerApiPage />,
        path: ROUTE_CONFIG.SWAGGER_API.path,
      },
      {
        element: <ConnectionsPage />,
        path: ROUTE_CONFIG.WALLET.children!.CONNECTIONS.path,
      },
      {
        element: <CredentialsPage />,
        path: ROUTE_CONFIG.WALLET.children!.CREDENTIALS.path,
      },
      {
        element: <IdentifiersPage />,
        path: ROUTE_CONFIG.WALLET.children!.IDENTIFIERS.path,
      },
      {
        element: <NotificationsPage />,
        path: ROUTE_CONFIG.NOTIFICATIONS.path,
      },
      {
        element: <SettingsPage />,
        path: ROUTE_CONFIG.SETTINGS.path,
      },
    ],
  },
  {
    path: '*',
    element: (
      <MainLayout>
        <PageNotFound />
      </MainLayout>
    ),
  },
    ],
  },
];

const routes = createBrowserRouter(router, {
  basename: constants.APP_BASENAME,
});

export default routes;
